import { type RefObject, useEffect, useState } from 'react';
import translations from '../../../translations';
import type { OrderDirection } from '../../../types/api';
import type { EnvironmentVariable } from '../../../types/environment';
import { stringReplace } from '../../../utils/string';
import { getTenantId } from '../../../utils/tenant';
import ButtonIcon from '../../buttons/ButtonIcon';
import FormGroup from '../../generic/FormGroup';
import SearchInput from '../../generic/SearchInput';
import Sortable from '../../generic/Sortable';
import Table, { type TableColumnList } from '../../generic/Table';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import Modal from '../../generic/modal/GenericModal';
import Toggle from '../../inputs/Toggle';
import CodeTypeEditor from '../../values/editor/CodeTypeEditor';
import { CODE_TYPES } from '../../values/editor/constants';
import { useEnvironments } from './EnvironmentsProvider';

export const EnvironmentValueKind = {
    string: 'String',
    code: 'Code',
};

type Props = {
    environmentId: string;
    isActive: boolean;
    modalContainerRef: RefObject<HTMLDivElement | null>;
};

const EnvironmentVariables = ({ environmentId, isActive, modalContainerRef }: Props) => {
    const [showVariableModal, setShowVariableModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [id, setId] = useState<string | null>(null);
    const [name, setName] = useState<string | null>(null);
    const [value, setValue] = useState<string | null>(null);
    const [valueKind, setValueKind] = useState(EnvironmentValueKind.string);
    const [codeType, setCodeType] = useState<string | null>(CODE_TYPES.yaml);
    const [isSecret, setIsSecret] = useState(false);
    const [isSecretToggleDisabled, setIsSecretToggleDisabled] = useState(false);
    const [valid, setValid] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState('name');
    const [sortDirection, setSortDirection] = useState<OrderDirection>('ASC');
    const [paging, updatePaging] = useState({
        page: 1,
        pageSize: 20,
        total: 0,
        searchTerm: '',
    });

    const {
        environmentVariablesLoading,
        environmentVariables,
        getEnvironmentVariables,
        saveEnvironmentVariable,
        deleteEnvironmentVariable,
    } = useEnvironments();

    useEffect(() => {
        if (isActive) {
            loadEnvironmentVariables({ page: 1 });
        }
    }, [isActive]);

    const loadEnvironmentVariables = async ({
        searchTerm = paging.searchTerm,
        page,
    }: { searchTerm?: string; page: number }) => {
        const request = {
            searchTerm,
            page,
            pageSize: paging.pageSize,
            orderBy: sortBy,
            orderDirection: sortDirection,
        };

        const results = await getEnvironmentVariables(environmentId, request);

        if (results) {
            updatePaging({
                ...paging,
                total: results._meta.total,
                pageSize: results._meta.pageSize,
                page: results._meta.page,
            });
        }
    };

    const onSave = async () => {
        if (name && value) {
            const variable: EnvironmentVariable = {
                id: id,
                name: name,
                value: value,
                valueKind: valueKind,
                isSecret: isSecret,
                codeType: valueKind === EnvironmentValueKind.code ? codeType : null,
                environmentId,
                tenantId: getTenantId(),
            };

            await saveEnvironmentVariable(environmentId, variable);
            setShowVariableModal(false);
            await loadEnvironmentVariables({ page: 1 });
        }
    };

    const resetPaging = () => {
        updatePaging({
            ...paging,
            page: 1,
            total: 0,
            searchTerm: '',
        });
        setSortBy('name');
        setSortDirection('ASC');
    };

    const resetForm = () => {
        setId(null);
        setName('');
        setValue('');
        setIsSecret(false);
        setIsSecretToggleDisabled(false);
        setValueKind(EnvironmentValueKind.string);
        setCodeType(CODE_TYPES.yaml);
    };

    const onNameChange = (newName: string) => {
        setName(newName);
        setValid(!!newName && !!value);
    };

    const onValueChange = (newValue: string) => {
        setValue(newValue);
        setValid(!!name && !!newValue);
    };

    const onSearchTermFilterChange = (searchTerm: string) => {
        loadEnvironmentVariables({ searchTerm, page: 1 });
    };

    const onSort = ({ orderBy, direction }: { orderBy: string; direction: OrderDirection }) => {
        setSortBy(orderBy);
        setSortDirection(direction);
        loadEnvironmentVariables({ page: 1 });
    };

    const deleteVariable = async () => {
        if (id) {
            await deleteEnvironmentVariable(environmentId, id);
            resetPaging();
            await loadEnvironmentVariables({ page: 1 });
            setShowDeleteModal(false);
        }
    };

    const columns: TableColumnList<EnvironmentVariable> = [
        {
            renderHeader: () => 'Actions',
            renderCell: ({ item }) => (
                <div className="flex">
                    <ButtonIcon
                        glyph="Edit"
                        aria-label={`Edit ${item.name}`}
                        onClick={() => {
                            setId(item.id);
                            setName(item.name);
                            setValue(item.value);
                            setIsSecret(item.isSecret);
                            setIsSecretToggleDisabled(item.isSecret);
                            setShowVariableModal(true);
                            setValid(!!item.name && !!item.value);
                            setValueKind(item.valueKind);
                            setCodeType(item.codeType);
                        }}
                        title={`Edit ${item.name}`}
                        iconClass="icon-medium"
                    />
                    <ButtonIcon
                        className="margin-left-small danger"
                        glyph="Delete"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => {
                            setId(item.id);
                            setName(item.name);
                            setShowDeleteModal(true);
                        }}
                        title={`Delete ${item.name}`}
                        iconClass="icon-medium"
                    />
                </div>
            ),
        },
        {
            renderHeader: () => (
                <Sortable
                    defaultDirection={'ASC'}
                    direction={sortBy === 'name' ? sortDirection : null}
                    onSort={(direction) => onSort({ orderBy: 'name', direction })}
                >
                    {translations.COMMON_TABLE_name}
                </Sortable>
            ),
            renderCell: ({ item }) => item.name,
            size: '30%',
        },
        {
            renderHeader: () => (
                <Sortable
                    defaultDirection={'ASC'}
                    direction={sortBy === 'value' ? sortDirection : null}
                    onSort={(direction) => onSort({ orderBy: 'value', direction })}
                >
                    {translations.COMMON_TABLE_value}
                </Sortable>
            ),
            renderCell: ({ item }) =>
                (item.value?.length ?? 0 > 190) ? item.value.substring(0, 190) : item.value,
            size: '65%',
        },
    ];

    return (
        <div>
            <div className="flex margin-bottom margin-top filterHeading padding">
                <SearchInput
                    className="tab-header-search inline-block"
                    value={paging.searchTerm}
                    onChange={onSearchTermFilterChange}
                />
            </div>
            <button
                className="btn btn-success btn-sm"
                onClick={() => {
                    resetForm();
                    setShowVariableModal(true);
                }}
                title={translations.ENVIRONMENT_new_environment_variable}
                type="button"
            >
                <span className="glyphicon glyphicon-plus" />
                <span>{translations.ENVIRONMENT_new_environment_variable}</span>
            </button>
            <Table
                tableClass="margin-top"
                items={environmentVariables}
                columns={columns}
                isLoading={environmentVariablesLoading}
                pagination={{
                    page: paging.page,
                    pageSize: paging.pageSize,
                    total: paging.total,
                    changePage: (pageNumber) => {
                        updatePaging({
                            ...paging,
                            page: pageNumber,
                        });
                        loadEnvironmentVariables({ page: pageNumber });
                    },
                }}
            />
            <Modal
                container={modalContainerRef.current}
                show={showVariableModal}
                onHide={() => setShowVariableModal(false)}
                title={
                    id
                        ? translations.ENVIRONMENT_edit_environment_variable
                        : translations.ENVIRONMENT_new_environment_variable
                }
                renderBody={() => (
                    <>
                        <FormGroup label="Name" htmlFor="environment-variable-name">
                            <input
                                id="environment-variable-name"
                                className="form-control form-control-dynamic"
                                type="text"
                                onChange={(e) => onNameChange(e.target.value)}
                                value={name || ''}
                            />
                        </FormGroup>

                        <FormGroup label={translations.ENVIRONMENT_value_kind} htmlFor="value-kind">
                            <select
                                id="value-kind"
                                value={valueKind}
                                onChange={({ target: { value } }) => {
                                    setValueKind(value);
                                }}
                                className="form-control form-control-width"
                                data-testid="value-kind-select"
                            >
                                <option value={EnvironmentValueKind.string}>
                                    {EnvironmentValueKind.string}
                                </option>
                                <option value={EnvironmentValueKind.code}>
                                    {EnvironmentValueKind.code}
                                </option>
                            </select>
                        </FormGroup>
                        {valueKind === EnvironmentValueKind.string ? (
                            <FormGroup label="Value" htmlFor="environment-variable-value">
                                <textarea
                                    id="environment-variable-value"
                                    className="form-control form-control-dynamic"
                                    onChange={(e) => onValueChange(e.target.value)}
                                    value={value || ''}
                                />
                            </FormGroup>
                        ) : (
                            <>
                                {codeType !== null && (
                                    <CodeTypeEditor
                                        editorLabel={translations.VALUE}
                                        codeType={codeType}
                                        value={value}
                                        onCodeTypeChange={(value) => setCodeType(value)}
                                        onCodeEditorChange={(value) => {
                                            setValid(!!name && !!value);
                                            setValue(value as string);
                                        }}
                                    />
                                )}
                            </>
                        )}

                        <label htmlFor="environment-variable-is-secret">
                            <Toggle
                                isOn={isSecret}
                                onChange={({ isOn }) => setIsSecret(isOn)}
                                testId="environment-variable-is-secret"
                                id="environment-variable-is-secret"
                                isDisabled={isSecretToggleDisabled}
                            />
                            Is Secret
                        </label>
                    </>
                )}
                className="config-modal"
                renderFooter={() => (
                    <>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={() => setShowVariableModal(false)}
                        >
                            {translations.ENVIRONMENT_cancel}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => onSave()}
                            disabled={!valid}
                        >
                            {translations.ENVIRONMENT_save}
                        </button>
                    </>
                )}
            />
            <ConfirmModal
                container={modalContainerRef.current}
                show={showDeleteModal}
                title={translations.ENVIRONMENT_delete_environment_variable_title}
                messages={[
                    stringReplace(translations.ENVIRONMENT_delete_environment_variable_message, {
                        variableName: name ?? '',
                    }),
                ]}
                buttonStyle="danger"
                buttonCaption="Delete"
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => {
                    deleteVariable();
                    setShowDeleteModal(false);
                }}
            />
        </div>
    );
};

export default EnvironmentVariables;
